import { Injectable } from '@angular/core';
import { IDEAMessageService, IDEATranslationsService } from '@idea-ionic/common';

import { environment as env } from '@env';

/**
 * To communicate with Global's API endpoint.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
    /**
     * The base URL to which to make requests.
     */
    baseURL = `https://${String(env.idea.api.url)}`;
    /**
     * Passed as `Authorization` header.
     */
    authToken: string;

    constructor(private t: IDEATranslationsService, private message: IDEAMessageService) {}

    /**
     * Execute an online API request.
     * @param path resource path (e.g. `['users', userId]`)
     * @param method HTTP method
     * @param options the request options
     */
    private async request(
        path: string[] | string,
        method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
        options: ApiRequestOptions = {}
    ): Promise<any> {
        const url = this.baseURL.concat('/', Array.isArray(path) ? path.join('/') : path);

        let headers: any = {};
        if (!options.bodyInFormData) headers['Content-Type'] = 'application/json';
        if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;
        headers = { ...headers, ...options.headers };

        const params = options.params ? '?'.concat(new URLSearchParams(options.params).toString()) : '';

        let body: any = null;
        if (options.body) {
            if (options.bodyInFormData) body = options.body;
            else body = JSON.stringify(options.body);
        }

        const res = await fetch(url.concat(params), { method, headers, body });

        // handle session timeout/expired token
        if (res.status === 401 || res.status === 403) {
            this.handleSessionTimeout();
            // return a promise that never resolves to prevent any downstream error handling
            return new Promise(() => {});
        }

        if (res.status !== 200) throw new Error('Operation failed (server)');

        const data = await res.json();

        // some API routes don't use the 200/400 ok/error system, but use an internal `bEsito` attribute
        if (!data.esito || data.esito.bEsito === true) return data;
        else {
            const errServer = data.esito?.listErrorMessage?.length > 0 ? data.esito.listErrorMessage[0] : {};
            throw new ApiError(errServer.txMessage ?? this.t._('COMMON.OPERATION_FAILED'), errServer.cdMessage ?? '-1');
        }
    }

    /**
     * Handle session timeout by redirecting to login with a warning.
     */
    private handleSessionTimeout(): void {
        localStorage.removeItem('AuthToken');
        this.authToken = null;

        this.message.error('AUTH.SESSION_EXPIRED');

        setTimeout(() => {
            window.location.href = '/auth/sign-in';
        }, 4000);
    }

    /**
     * GET request.
     */
    async getResource(path: string[] | string, options?: ApiRequestOptions): Promise<any> {
        return await this.request(path, 'GET', options);
    }

    /**
     * POST request.
     */
    async postResource(path: string[] | string, options?: ApiRequestOptions): Promise<any> {
        return await this.request(path, 'POST', options);
    }

    /**
     * PUT request.
     */
    async putResource(path: string[] | string, options?: ApiRequestOptions): Promise<any> {
        return await this.request(path, 'PUT', options);
    }

    /**
     * PATCH request.
     */
    async patchResource(path: string[] | string, options?: ApiRequestOptions): Promise<any> {
        return await this.request(path, 'PATCH', options);
    }

    /**
     * DELETE request.
     */
    async deleteResource(path: string[] | string, options?: ApiRequestOptions): Promise<any> {
        return await this.request(path, 'DELETE', options);
    }
}

/**
 * The options of an API request.
 */
interface ApiRequestOptions {
    /**
     * The additional headers of the request; `Authorization` and `X-API-Key` are included by default, if set.
     */
    headers?: { [key: string]: string | number | boolean };
    /**
     * The query parameters of the request.
     */
    params?: any;
    /**
     * The body of the request.
     */
    body?: any;
    /**
     * Whether the body is in FormData.
     */
    bodyInFormData?: boolean;
}

/**
 * Error generared by the API; it tracks also the error code.
 */
export class ApiError extends Error {
    code: string;

    constructor(message: string, code: string) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
    }
}
